import { useMemo, useState, useEffect, useCallback } from "react";
import {
  TOURS_ENABLED,
  hasTourBeenSeen,
  peekPendingTour,
  consumePendingTour,
} from "@/lib/tourState";
import {
  buildPropertyDetailTourSteps,
  type PropertyDetailTourContext,
} from "@/lib/propertyDetailTourSteps";

const SCHEDULE_VIEW_KEY = "schedule_view";
import type { PropertyTabId } from "@/lib/propertyTabs";
import type { Step } from "react-joyride";
import { useCleaningTasks } from "@/hooks/useCleaningTasks";
import { useReservations } from "@/hooks/useReservations";
import { useChecklists } from "@/hooks/useChecklists";
import { useInventoryItems } from "@/hooks/useInventory";

const TOUR_ID = "owner-property-detail";

export function usePropertyDetailTour(
  propertyId: string | undefined,
  property: {
    airbnb_ical_url?: string | null;
    vrbo_ical_url?: string | null;
    booking_ical_url?: string | null;
  } | null,
  allowedTabs: PropertyTabId[],
  onTabChange: (tab: PropertyTabId) => void
) {
  const [runTour, setRunTour] = useState(false);

  const { data: tasks = [] } = useCleaningTasks(TOURS_ENABLED ? propertyId : undefined);
  const { data: reservations = [] } = useReservations(TOURS_ENABLED ? propertyId : undefined);
  const { data: checklists = [] } = useChecklists(TOURS_ENABLED ? propertyId : undefined);
  const { data: inventory = [] } = useInventoryItems(TOURS_ENABLED ? propertyId : undefined);

  const context = useMemo<PropertyDetailTourContext>(
    () => ({
      hasIcal: Boolean(
        property?.airbnb_ical_url ||
          property?.vrbo_ical_url ||
          property?.booking_ical_url
      ),
      hasScheduleEntries:
        (reservations?.length ?? 0) > 0 || (tasks?.length ?? 0) > 0,
      hasChecklists: (checklists?.length ?? 0) > 0,
      hasInventoryItems: (inventory?.length ?? 0) > 0,
      allowedTabs,
    }),
    [
      property?.airbnb_ical_url,
      property?.vrbo_ical_url,
      property?.booking_ical_url,
      reservations?.length,
      tasks?.length,
      checklists?.length,
      inventory?.length,
      allowedTabs,
    ]
  );

  const steps = useMemo(
    () => buildPropertyDetailTourSteps(context, onTabChange, propertyId),
    [context, onTabChange, propertyId]
  );

  useEffect(() => {
    if (!TOURS_ENABLED) return;
    if (!propertyId || !property || steps.length === 0) return;

    const timer = setTimeout(() => {
      const pending = peekPendingTour();
      if (pending && pending !== TOUR_ID) return;

      let forceFromPending = false;
      if (pending === TOUR_ID) {
        consumePendingTour();
        forceFromPending = true;
      }

      if (forceFromPending || !hasTourBeenSeen(TOUR_ID)) {
        if (propertyId) {
          try {
            localStorage.setItem(`${SCHEDULE_VIEW_KEY}:${propertyId}`, "timeline");
          } catch {
            /* ignore */
          }
        }
        onTabChange("overview");
        setRunTour(true);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [propertyId, property, steps.length, onTabChange]);

  const closeTour = useCallback(() => setRunTour(false), []);

  return {
    runTour: TOURS_ENABLED && runTour,
    steps: TOURS_ENABLED && runTour ? steps : ([] as Step[]),
    closeTour,
  };
}
